import "react-loading-skeleton/dist/skeleton.css";

import { CurrentAccountIdProvider } from "@zapier/identity";
import { Colors, GlobalStylesProvider } from "@zapier/design-system";
import { ThemeProvider, GlobalStyle } from "../lib/theme";
import UserProvider from "lib/context/user-context";
import React, { Suspense } from "react";
import { PageErrorBoundary } from "components/PageErrorBoundary";
import { Toaster } from "lib/react-hot-toast";
import { DndProvider } from "lib/react-dnd";
import FullStory from "../observability/FullStory";
import { MixpanelContextProvider } from "../observability/mixpanel";
import { TrackingProvider } from "observability";
import { ConfirmModalProvider } from "components/ModalConfirmation";
import { GlobalHead } from "components/GlobalHead";
import NextNProgress from "nextjs-progressbar";
import { MaintenancePage } from "components/MaintenancePage";
import { SkeletonTheme } from "components/SkeletonTheme";
import useMaintenance from "lib/hooks/useMaintenance";
import BuilderPageSpinner from "./BuilderPageSpinner";
import AdvancedAccountSelector from "components/AdvancedAccountSelector";

type Props = {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  hideProgress?: boolean;
  omitNestedProviders?: boolean;
  omitUserProvider?: boolean;
  skipPageViewEvent?: boolean;
  user?: any;
};

export default function AppProvider({
  children,
  fallback = <BuilderPageSpinner />,
  hideProgress = false,
  omitNestedProviders = false,
  omitUserProvider = false,
  skipPageViewEvent = false,
  user,
}: Props) {
  return (
    <>
      <GlobalHead />
      {!hideProgress && (
        <NextNProgress
          color={Colors.blueJeans}
          stopDelayMs={0}
          height={2}
          options={{ showSpinner: false }}
        />
      )}
      <GlobalStylesProvider isPlatform={true} isRebrand={true}>
        <ThemeProvider>
          <GlobalStyle />
          <SkeletonTheme>
            {!omitNestedProviders ? (
              <PageErrorBoundary>
                <Suspense fallback={fallback}>
                  <MaintenancePage useMaintenanceHook={useMaintenance}>
                    <CurrentAccountIdProvider>
                      <DndProvider>
                        <ConfirmModalProvider>
                          {!omitUserProvider ? (
                            <UserProvider fallback={fallback} user={user}>
                              <FullStory />
                              <AdvancedAccountSelector />
                              <TrackingProvider
                                skipPageViewEvent={skipPageViewEvent}
                              >
                                <MixpanelContextProvider>
                                  {children}
                                </MixpanelContextProvider>
                              </TrackingProvider>
                            </UserProvider>
                          ) : (
                            children
                          )}
                        </ConfirmModalProvider>
                      </DndProvider>
                    </CurrentAccountIdProvider>
                  </MaintenancePage>
                </Suspense>
              </PageErrorBoundary>
            ) : (
              children
            )}
            <Toaster />
          </SkeletonTheme>
        </ThemeProvider>
      </GlobalStylesProvider>
    </>
  );
}
